/**
 * PintsPresets - Pints & Pegs Drinking Board Presets & Layout Engine
 * Provides curated drinking board presets (Pub Crawl, Power Hour, Social Hour, Battle of the Sexes)
 * and custom layout CRUD management persisted in IndexedDB.
 */

import { appStorage } from '../storage/IndexedDBStorage.js';
import { LayoutSanitizer, MAX_CUSTOM_LAYOUTS } from './LayoutSanitizer.js';

export const PINTS_CUSTOM_LAYOUTS_KEY = 'cc_pints_custom_layouts';

export const PINTS_PRESET_KEYS = Object.freeze({
  STANDARD: 'standard',
  POWER_HOUR: 'powerHour',
  SOCIAL_HOUR: 'socialHour',
  BATTLE_OF_SEXES: 'battleOfSexes'
});

export const PINTS_PRESETS_121 = Object.freeze({
  standard: {
    key: 'standard',
    name: 'Pub Crawl',
    badge: '🍻 Balanced',
    icon: '🍻',
    description: 'The classic pub crawl around the board. A smooth, fun balance of personal drinks, neighbor penalties, and social toasts.',
    tags: ['Balanced', 'Party', 'Classic'],
    drinkSpaces: 16,
    config: {
      optYouDrink: true,
      optYouDrinkCount: 2,
      optYouDrinkPegs: 1,
      optYouDrink1Hole: 15,
      optYouDrink2Hole: 75,

      optGuysDrink: true,
      optGuysDrinkCount: 2,
      optGuysDrinkPegs: 1,
      optGuysDrink1Hole: 25,
      optGuysDrink2Hole: 85,

      optGalsDrink: true,
      optGalsDrinkCount: 2,
      optGalsDrinkPegs: 1,
      optGalsDrink1Hole: 35,
      optGalsDrink2Hole: 95,

      optLeftDrink: true,
      optLeftDrinkCount: 2,
      optLeftDrinkPegs: 1,
      optLeftDrink1Hole: 10,
      optLeftDrink2Hole: 70,

      optRightDrink: true,
      optRightDrinkCount: 2,
      optRightDrinkPegs: 1,
      optRightDrink1Hole: 20,
      optRightDrink2Hole: 80,

      optEveryoneDrink: true,
      optEveryoneDrinkCount: 2,
      optEveryoneDrinkPegs: 1,
      optEveryoneDrink1Hole: 30,
      optEveryoneDrink2Hole: 90,

      optEveryoneButYouDrink: true,
      optEveryoneButYouDrinkCount: 2,
      optEveryoneButYouDrinkPegs: 1,
      optEveryoneButYouDrink1Hole: 45,
      optEveryoneButYouDrink2Hole: 105,

      optPickWhoDrinks: true,
      optPickWhoDrinksCount: 2,
      optPickWhoDrinksPegs: 1,
      optPickWhoDrinks1Hole: 55,
      optPickWhoDrinks2Hole: 115
    }
  },

  powerHour: {
    key: 'powerHour',
    name: 'Power Hour',
    badge: '⚡ High Stakes',
    icon: '⚡',
    description: 'Intense and relentless drinking action! Packed with rapid personal shots, neighbor penalties, and cutthroat player targeting.',
    tags: ['Intense', 'Fast', 'Heavy Drinking'],
    drinkSpaces: 24,
    config: {
      optYouDrink: true,
      optYouDrinkCount: 4,
      optYouDrinkPegs: 2,
      optYouDrink1Hole: 10,
      optYouDrink2Hole: 40,
      optYouDrink3Hole: 70,
      optYouDrink4Hole: 100,

      optGuysDrink: true,
      optGuysDrinkCount: 2,
      optGuysDrinkPegs: 1,
      optGuysDrink1Hole: 20,
      optGuysDrink2Hole: 80,

      optGalsDrink: true,
      optGalsDrinkCount: 2,
      optGalsDrinkPegs: 1,
      optGalsDrink1Hole: 25,
      optGalsDrink2Hole: 85,

      optLeftDrink: true,
      optLeftDrinkCount: 3,
      optLeftDrinkPegs: 1,
      optLeftDrink1Hole: 15,
      optLeftDrink2Hole: 50,
      optLeftDrink3Hole: 95,

      optRightDrink: true,
      optRightDrinkCount: 3,
      optRightDrinkPegs: 1,
      optRightDrink1Hole: 30,
      optRightDrink2Hole: 65,
      optRightDrink3Hole: 105,

      optEveryoneDrink: true,
      optEveryoneDrinkCount: 3,
      optEveryoneDrinkPegs: 1,
      optEveryoneDrink1Hole: 35,
      optEveryoneDrink2Hole: 75,
      optEveryoneDrink3Hole: 115,

      optEveryoneButYouDrink: true,
      optEveryoneButYouDrinkCount: 3,
      optEveryoneButYouDrinkPegs: 1,
      optEveryoneButYouDrink1Hole: 45,
      optEveryoneButYouDrink2Hole: 85,
      optEveryoneButYouDrink3Hole: 110,

      optPickWhoDrinks: true,
      optPickWhoDrinksCount: 4,
      optPickWhoDrinksPegs: 2,
      optPickWhoDrinks1Hole: 5,
      optPickWhoDrinks2Hole: 55,
      optPickWhoDrinks3Hole: 90,
      optPickWhoDrinks4Hole: 118
    }
  },

  socialHour: {
    key: 'socialHour',
    name: 'Social Hour',
    badge: '🥂 Group Socials',
    icon: '🥂',
    description: 'High-energy party atmosphere! Maximizes table-wide toasts ("Everyone Drinks") and "Everyone But You" cheers.',
    tags: ['Social', 'Celebration', 'Group Fun'],
    drinkSpaces: 18,
    config: {
      optYouDrink: true,
      optYouDrinkCount: 2,
      optYouDrinkPegs: 1,
      optYouDrink1Hole: 12,
      optYouDrink2Hole: 72,

      optGuysDrink: true,
      optGuysDrinkCount: 1,
      optGuysDrinkPegs: 1,
      optGuysDrink1Hole: 32,

      optGalsDrink: true,
      optGalsDrinkCount: 1,
      optGalsDrinkPegs: 1,
      optGalsDrink1Hole: 42,

      optLeftDrink: true,
      optLeftDrinkCount: 2,
      optLeftDrinkPegs: 1,
      optLeftDrink1Hole: 22,
      optLeftDrink2Hole: 82,

      optRightDrink: true,
      optRightDrinkCount: 2,
      optRightDrinkPegs: 1,
      optRightDrink1Hole: 52,
      optRightDrink2Hole: 92,

      optEveryoneDrink: true,
      optEveryoneDrinkCount: 4,
      optEveryoneDrinkPegs: 1,
      optEveryoneDrink1Hole: 15,
      optEveryoneDrink2Hole: 45,
      optEveryoneDrink3Hole: 75,
      optEveryoneDrink4Hole: 105,

      optEveryoneButYouDrink: true,
      optEveryoneButYouDrinkCount: 4,
      optEveryoneButYouDrinkPegs: 1,
      optEveryoneButYouDrink1Hole: 25,
      optEveryoneButYouDrink2Hole: 55,
      optEveryoneButYouDrink3Hole: 85,
      optEveryoneButYouDrink4Hole: 115,

      optPickWhoDrinks: true,
      optPickWhoDrinksCount: 2,
      optPickWhoDrinksPegs: 1,
      optPickWhoDrinks1Hole: 35,
      optPickWhoDrinks2Hole: 95
    }
  },

  battleOfSexes: {
    key: 'battleOfSexes',
    name: 'Battle of the Sexes',
    badge: '🥊 Guys vs Gals',
    icon: '🥊',
    description: 'Alternating Guys Drink vs Gals Drink battlefields! Keep score and drink when your gender space is hit.',
    tags: ['Battle', 'Competitive', 'Guys vs Gals'],
    drinkSpaces: 20,
    config: {
      optYouDrink: true,
      optYouDrinkCount: 2,
      optYouDrinkPegs: 1,
      optYouDrink1Hole: 20,
      optYouDrink2Hole: 80,

      optGuysDrink: true,
      optGuysDrinkCount: 4,
      optGuysDrinkPegs: 1,
      optGuysDrink1Hole: 10,
      optGuysDrink2Hole: 40,
      optGuysDrink3Hole: 70,
      optGuysDrink4Hole: 100,

      optGalsDrink: true,
      optGalsDrinkCount: 4,
      optGalsDrinkPegs: 1,
      optGalsDrink1Hole: 15,
      optGalsDrink2Hole: 45,
      optGalsDrink3Hole: 75,
      optGalsDrink4Hole: 105,

      optLeftDrink: true,
      optLeftDrinkCount: 2,
      optLeftDrinkPegs: 1,
      optLeftDrink1Hole: 30,
      optLeftDrink2Hole: 90,

      optRightDrink: true,
      optRightDrinkCount: 2,
      optRightDrinkPegs: 1,
      optRightDrink1Hole: 35,
      optRightDrink2Hole: 95,

      optEveryoneDrink: true,
      optEveryoneDrinkCount: 2,
      optEveryoneDrinkPegs: 1,
      optEveryoneDrink1Hole: 60,
      optEveryoneDrink2Hole: 120,

      optEveryoneButYouDrink: true,
      optEveryoneButYouDrinkCount: 1,
      optEveryoneButYouDrinkPegs: 1,
      optEveryoneButYouDrink1Hole: 50,

      optPickWhoDrinks: true,
      optPickWhoDrinksCount: 3,
      optPickWhoDrinksPegs: 1,
      optPickWhoDrinks1Hole: 25,
      optPickWhoDrinks2Hole: 65,
      optPickWhoDrinks3Hole: 115
    }
  }
});

export class PintsPresets {
  static getStorage(storage = null) {
    if (storage) return storage;
    if (typeof window !== 'undefined' && window.appStorage) return window.appStorage;
    return appStorage;
  }

  static getPresets(target = 121) {
    const t = Number(target) || 121;
    if (t === 121) return PINTS_PRESETS_121;
    return PintsPresets.scalePresetsForTarget(PINTS_PRESETS_121, t);
  }

  static scalePresetsForTarget(presets, target) {
    const scale = (target - 1) / 120;
    const scaled = {};

    for (const [key, preset] of Object.entries(presets)) {
      const cfg = { ...preset.config };
      for (const [prop, val] of Object.entries(cfg)) {
        if (typeof val === 'number' && prop.endsWith('Hole')) {
          cfg[prop] = Math.min(target - 2, Math.max(2, Math.round(val * scale)));
        }
      }
      scaled[key] = {
        ...preset,
        config: cfg
      };
    }
    return scaled;
  }

  static computeTrackMetrics(items = [], target = 121) {
    const total = items.length;
    let socials = 0;
    let targeted = 0;
    let gender = 0;

    const holes = [];
    for (const item of items) {
      if (item.hole != null) holes.push(item.hole);
      if (['everyoneDrink', 'everyoneButYouDrink'].includes(item.type)) socials++;
      else if (['guysDrink', 'galsDrink'].includes(item.type)) gender++;
      else targeted++;
    }

    holes.sort((a, b) => a - b);
    let avgSpacing = 0;
    if (holes.length > 1) {
      let sumDist = 0;
      for (let i = 1; i < holes.length; i++) {
        sumDist += (holes[i] - holes[i - 1]);
      }
      avgSpacing = Math.round(sumDist / (holes.length - 1));
    } else if (holes.length === 1) {
      avgSpacing = target;
    }

    return {
      totalDrinkSpaces: total,
      socialDrinks: socials,
      targetedDrinks: targeted,
      genderDrinks: gender,
      avgSpacing
    };
  }

  static applyPreset(presetKey, context = {}) {
    const target = (context.settings && context.settings.target) || (typeof window !== 'undefined' && window.settings && window.settings.target) || 121;
    const presets = PintsPresets.getPresets(target);
    const preset = presets[presetKey] || presets.standard;
    if (!preset) return false;

    const cfg = preset.config;
    const doc = context.document || (typeof document !== 'undefined' ? document : null);

    if (doc) {
      for (const [prop, val] of Object.entries(cfg)) {
        const el = doc.getElementById(prop);
        if (!el) continue;
        if (el.type === 'checkbox') {
          el.checked = Boolean(val);
          const toggleFn = (typeof window !== 'undefined') ? window[`toggle${prop.replace('opt', '')}Config`] : null;
          if (typeof toggleFn === 'function') toggleFn(el.checked);
        } else {
          el.value = String(val);
          el.dataset.customized = 'true';
        }
      }
      if (typeof window !== 'undefined' && typeof window.updateTrackItemRows === 'function') {
        const keys = ['youDrink', 'guysDrink', 'galsDrink', 'leftDrink', 'rightDrink', 'everyoneDrink', 'everyoneButYouDrink', 'pickWhoDrinks'];
        keys.forEach(k => window.updateTrackItemRows(k));
      }
    }

    if (context.settings || (typeof window !== 'undefined' && window.settings)) {
      const s = context.settings || window.settings;
      Object.assign(s, cfg);
      s.pintsPreset = presetKey;
    }
    if (typeof window !== 'undefined' && typeof window.saveBrandSettings === 'function' && typeof window.readBrandSettings === 'function') {
      window.saveBrandSettings('pints', window.readBrandSettings('pints'));
    }
    return true;
  }

  static getCustomLayouts(storage = null) {
    const s = PintsPresets.getStorage(storage);
    try {
      const raw = s.getItem(PINTS_CUSTOM_LAYOUTS_KEY);
      if (!raw) return [];
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn('[PintsPresets] Error loading custom layouts:', e);
      return [];
    }
  }

  static saveCustomLayout(layout, storage = null, isImport = false) {
    if (!layout || typeof layout !== 'object') throw new Error('Invalid layout object');
    const s = PintsPresets.getStorage(storage);
    const layouts = PintsPresets.getCustomLayouts(s);

    const now = new Date().toISOString();
    const sanitized = LayoutSanitizer.sanitizeLayout(layout, 'pints', {
      regenerateId: isImport || !layout.id
    });
    sanitized.metrics = PintsPresets.computeTrackMetrics(sanitized.items, sanitized.target);

    let targetLayout = null;
    if (!isImport && layout.id) {
      const idx = layouts.findIndex(l => l.id === sanitized.id);
      if (idx !== -1) {
        targetLayout = {
          ...layouts[idx],
          ...sanitized,
          updatedAt: now
        };
        layouts[idx] = targetLayout;
      }
    }

    if (!targetLayout) {
      targetLayout = sanitized;
      layouts.unshift(targetLayout);
    }

    const capped = LayoutSanitizer.enforceStorageQuota(layouts, MAX_CUSTOM_LAYOUTS);

    if (s) {
      try {
        s.setItem(PINTS_CUSTOM_LAYOUTS_KEY, JSON.stringify(capped));
      } catch (e) {
        console.error('[PintsPresets] Failed to save custom layouts:', e);
        throw new Error('Failed to save layout to storage: Quota exceeded or storage unavailable.');
      }
    }
    return targetLayout;
  }

  static renameCustomLayout(layoutId, newName, storage = null) {
    if (!layoutId || !newName || !newName.trim()) return false;
    const s = PintsPresets.getStorage(storage);
    const layouts = PintsPresets.getCustomLayouts(s);
    const layout = layouts.find(l => l.id === layoutId);
    if (!layout) return false;

    layout.name = newName.trim();
    layout.updatedAt = new Date().toISOString();
    s.setItem(PINTS_CUSTOM_LAYOUTS_KEY, JSON.stringify(layouts));
    return true;
  }

  static deleteCustomLayout(layoutId, storage = null) {
    if (!layoutId) return false;
    const s = PintsPresets.getStorage(storage);
    const layouts = PintsPresets.getCustomLayouts(s);
    const filtered = layouts.filter(l => l.id !== layoutId);
    if (filtered.length === layouts.length) return false;
    s.setItem(PINTS_CUSTOM_LAYOUTS_KEY, JSON.stringify(filtered));
    return true;
  }

  static duplicateCustomLayout(layoutId, storage = null) {
    if (!layoutId) return null;
    const s = PintsPresets.getStorage(storage);
    const layouts = PintsPresets.getCustomLayouts(s);
    const original = layouts.find(l => l.id === layoutId);
    if (!original) return null;

    const copy = {
      ...original,
      id: undefined,
      name: `${original.name} (Copy)`
    };
    return PintsPresets.saveCustomLayout(copy, s);
  }

  static exportLayout(layoutId = null, storage = null) {
    const s = PintsPresets.getStorage(storage);
    const layouts = PintsPresets.getCustomLayouts(s);
    if (layoutId) {
      const single = layouts.find(l => l.id === layoutId);
      const prepared = LayoutSanitizer.prepareForExport(single || null);
      return JSON.stringify(prepared, null, 2);
    }
    const prepared = LayoutSanitizer.prepareForExport(layouts);
    return JSON.stringify(prepared, null, 2);
  }

  static importLayout(jsonStr, storage = null) {
    const parsed = LayoutSanitizer.parseJsonSafely(jsonStr);
    const s = PintsPresets.getStorage(storage);
    if (Array.isArray(parsed)) {
      if (parsed.length > 20) {
        throw new Error('Cannot import more than 20 layouts at once.');
      }
      let lastSaved = null;
      for (const item of parsed) {
        lastSaved = PintsPresets.saveCustomLayout(item, s, true);
      }
      return lastSaved;
    }
    return PintsPresets.saveCustomLayout(parsed, s, true);
  }
}

if (typeof window !== 'undefined') {
  window.PintsPresets = PintsPresets;
  window.PINTS_PRESETS_121 = PINTS_PRESETS_121;
}
